import { useState, useCallback, memo, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button } from "src/@/components/ui/button";
import { Card } from "src/@/components/ui/card";
import Iconify from 'src/components/Iconify';
import ChatInternoMessagesList from './ChatInternoMessagesList';
import ChatInternoBottombar from './ChatInternoBottombar';
import { useSelector, useDispatch } from 'react-redux';
import useAuth from 'src/hooks/useAuth';
import { useNavigate } from 'react-router';
import { getChatInternoBuscarChat, setSelectedChat, setRemoveRoomUnread, setLimparChatStaff } from 'src/redux/slices/staff';
import { clearSessionStorageItem, getSessionStorageItem } from 'src/services/sessionStorage';

// Design System: Action Button Component
const ItemButtonWindow = memo(({ title, onClick, icon, destructive }) => {
  return (
    <Button
      onClick={onClick}
      className={`h-8 w-8 rounded-lg p-0 ${destructive
        ? 'text-white/70 hover:text-red-200 hover:bg-white/10'
        : 'text-white/70 hover:text-white hover:bg-white/10'
        }`}
      variant="ghost"
      aria-label={title}
    >
      <Iconify icon={icon} width={16} height={16} />
    </Button>
  );
});

ItemButtonWindow.propTypes = {
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.string.isRequired,
  destructive: PropTypes.bool,
};

const ChatInternoMiniChat = ({ chat }) => {
  const { t } = useTranslation();
  const [isMinimized, setIsMinimized] = useState(false);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [localUnreadCount, setLocalUnreadCount] = useState(0);

  const { roomsWithNewMessages, staff, isLoading } = useSelector((state) => state.staff);

  const unreadCount = useMemo(() => {
    if (!chat || !roomsWithNewMessages) return 0;
    const reduxCount = roomsWithNewMessages.filter(id => id === chat._id).length;
    return reduxCount > 0 ? reduxCount : localUnreadCount;
  }, [chat, roomsWithNewMessages, localUnreadCount]);

  const otherUnreadCount = useMemo(() => {
    if (!chat || !roomsWithNewMessages) return 0;
    return roomsWithNewMessages.filter(id => id !== chat._id).length;
  }, [chat, roomsWithNewMessages]);

  const titleGroup = useMemo(() => {
    if (!chat) return '';
    if (chat?.isGroup) return chat.title;
    const other = chat?.people?.find(p => p._id !== user?.id);
    if (!other) return chat?.people?.[0]?.nomeFantasia || '';
    return other.firstName
      ? `${other.firstName} ${other.lastName || ''}`.trim()
      : other.nomeFantasia || '';
  }, [chat?._id, chat?.title, chat?.people, user?.id]);

  const handleMinimize = useCallback((e) => {
    e.stopPropagation();
    setIsMinimized(prev => {
      if (prev) setLocalUnreadCount(0); // Limpa unread ao expandir
      return !prev;
    });
  }, []);

  const handleNavToChat = useCallback((e) => {
    e.stopPropagation();
    try {
      const target = `/chats/interno/${chat._id}`;
      navigate(target);
      dispatch(getChatInternoBuscarChat(chat._id));
      dispatch(setSelectedChat(chat));
    } catch (err) {
      setError(t('chat.internal.miniChat.navigateError'));
      console.error('Erro na navegação:', err);
    }
  }, [chat._id, navigate, dispatch, t]);

  const handleClose = useCallback((e) => {
    e.stopPropagation();
    try {
      clearSessionStorageItem('chatInternoMiniId');
      dispatch(setLimparChatStaff());
      dispatch(setSelectedChat(null));
      setIsVisible(false);
    } catch (err) {
      setError(t('chat.internal.miniChat.closeError'));
      console.error('Erro ao fechar:', err);
    }
  }, [t, dispatch]);

  useEffect(() => {
    try {
      const chatData = getSessionStorageItem('chatInternoMiniId');
      if (chatData) {
        setIsVisible(true);
        setIsMinimized(true);
      }
    } catch (err) {
      console.error('Erro ao carregar chat do storage:', err);
      clearSessionStorageItem('chatInternoMiniId');
    }
  }, []);

  useEffect(() => {
    if (chat) {
      setIsVisible(true);
      setIsMinimized(false);
      setLocalUnreadCount(0);
    }
  }, [chat?._id]);

  // Limpa unread quando o chat está expandido (não minimizado)
  useEffect(() => {
    if (!isMinimized && chat?._id && unreadCount > 0) {
      dispatch(setRemoveRoomUnread(chat._id));
    }
  }, [isMinimized, chat?._id, unreadCount, dispatch]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      try {
        if (event.detail?.key === 'chatInternoMiniId' && event.detail?.value) {
          const newChatData = event.detail.value;
          const isSameChat = chat?._id && newChatData._id === chat._id;
          setIsVisible(true);
          if (isSameChat) {
            // Mesmo chat: incrementa contador local se minimizado
            setIsMinimized(prev => {
              if (prev) setLocalUnreadCount(c => c + 1);
              return prev;
            });
          } else {
            // Chat diferente (ação do usuário): expande
            setLocalUnreadCount(0);
            setIsMinimized(false);
          }
        }
      } catch (err) {
        console.error('Erro ao processar mudança no storage:', err);
      }
    };

    const handleNativeStorage = () => {
      const chatData = getSessionStorageItem('chatInternoMiniId');
      if (chatData) {
        const isSameChat = chat?._id && chatData._id === chat._id;
        setIsVisible(true);
        if (isSameChat) {
          setIsMinimized(prev => {
            if (prev) setLocalUnreadCount(c => c + 1);
            return prev;
          });
        } else {
          setLocalUnreadCount(0);
          setIsMinimized(false);
        }
      }
    };

    window.addEventListener('sessionStorageChange', handleStorageChange);
    window.addEventListener('storage', handleNativeStorage);

    return () => {
      window.removeEventListener('sessionStorageChange', handleStorageChange);
      window.removeEventListener('storage', handleNativeStorage);
    };
  }, [chat?._id]);

  if (error) {
    return (
      <div className="fixed bottom-0 right-4 z-50 p-4 bg-red-50 text-red-600 border border-red-200 border-b-radius-none shadow-lg text-sm mb-4">
        {error}
      </div>
    );
  }

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 right-[72px] z-[39]">
      <Card
        className={`bg-white shadow-2xl shadow-black/20 border border-gray-200/80 ring-1 ring-black/5 rounded-t-2xl rounded-b-none flex flex-col overflow-hidden ${isMinimized ? 'h-14 w-64' : 'h-[500px] w-64 sm:w-[308px]'
          }`}
      >
        {/* Header */}
        <div
          onClick={handleMinimize}
          className={`relative flex shrink-0 cursor-pointer items-center justify-between px-3 py-2 h-[55px] bg-gradient-to-b from-[#1a0a2e] via-[#160826] to-[#120620] dark:from-[#10051c] dark:via-[#0d0418] dark:to-[#0a0314] rounded-t-2xl ${unreadCount > 0 ? 'animate-pulse' : ''}`}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === 'Enter' && handleMinimize()}
          aria-label={isMinimized ? t('chat.internal.miniChat.expandChat') : t('chat.internal.miniChat.minimizeChat')}
        >
          <div className="flex items-center gap-2.5 overflow-hidden">
                {unreadCount > 0 && (
                <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">{unreadCount}</span>
              ) }
            <div className="overflow-hidden">
               {otherUnreadCount > 0 && (
                <span className="text-[10px] text-white/70 truncate block">
                  {otherUnreadCount} {otherUnreadCount === 1 ? t('chat.internal.miniChat.otherMessageSingular') : t('chat.internal.miniChat.otherMessagePlural')}
                </span>
              )}
              <span className='text-[12px] text-white truncate block'>
                {titleGroup}
              </span>
             
            </div>
          </div>

          <div className='flex items-center ml-1'>
            <ItemButtonWindow
              title={t('chat.internal.miniChat.openChat')}
              onClick={handleNavToChat}
              icon="eva:expand-outline"
            />
            <ItemButtonWindow
              title={isMinimized ? t('chat.internal.miniChat.expand') : t('chat.internal.miniChat.minimize')}
              onClick={handleMinimize}
              icon={isMinimized ? "eva:chevron-up-outline" : "eva:chevron-down-outline"}
            />
            <ItemButtonWindow
              title={t('common.buttons.close')}
              onClick={handleClose}
              icon="eva:close-outline"
              destructive
            />
          </div>
        </div>

        {/* Content Area */}
        {!isMinimized && (
          <div
            className='flex flex-col flex-1 h-full min-h-0 bg-gray-50/50'
          >
            {isLoading ? (
              <div className="flex-1 flex flex-col gap-3 p-4 animate-pulse">
                <div className="flex items-end gap-2">
                  <div className="w-6 h-6 rounded-full bg-gray-200 shrink-0" />
                  <div className="flex flex-col gap-1.5">
                    <div className="h-3 w-32 bg-gray-200 rounded-full" />
                    <div className="h-8 w-44 bg-gray-200 rounded-xl rounded-tl-sm" />
                  </div>
                </div>
                <div className="flex items-end gap-2 self-end">
                  <div className="flex flex-col gap-1.5 items-end">
                    <div className="h-8 w-36 bg-violet-100 rounded-xl rounded-tr-sm" />
                    <div className="h-3 w-16 bg-gray-200 rounded-full" />
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <div className="w-6 h-6 rounded-full bg-gray-200 shrink-0" />
                  <div className="flex flex-col gap-1.5">
                    <div className="h-3 w-24 bg-gray-200 rounded-full" />
                    <div className="h-12 w-48 bg-gray-200 rounded-xl rounded-tl-sm" />
                  </div>
                </div>
                <div className="flex items-end gap-2 self-end">
                  <div className="flex flex-col gap-1.5 items-end">
                    <div className="h-6 w-40 bg-violet-100 rounded-xl rounded-tr-sm" />
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <div className="w-6 h-6 rounded-full bg-gray-200 shrink-0" />
                  <div className="flex flex-col gap-1.5">
                    <div className="h-10 w-40 bg-gray-200 rounded-xl rounded-tl-sm" />
                  </div>
                </div>
                <div className="mt-auto pt-3 border-t border-gray-100">
                  <div className="h-9 w-full bg-gray-100 rounded-xl" />
                </div>
              </div>
            ) : (
              <>
                <ChatInternoMessagesList isMiniChat />
                <div className="flex-shrink-0 bg-white">
                  <ChatInternoBottombar chat={chat} isMiniChat />
                </div>
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

ChatInternoMiniChat.propTypes = {
  chat: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    isGroup: PropTypes.bool,
    title: PropTypes.string,
    people: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string,
      nomeFantasia: PropTypes.string
    }))
  }).isRequired,
};

export default memo(ChatInternoMiniChat);
